import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Tag, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import useCart from '@/store/cartStore';
import { toast } from 'sonner';

const ProductCard = ({ product }) => {
  const addItem = useCart((state) => state.addItem);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const success = await addItem(product, 1);
    if (success) {
      toast.success(`${product.name} added to cart!`, { duration: 1000 });
    } else {
      toast.error('Failed to add item to cart');
    }
  };

  const displayPrice = product.sale_price || product.price;
  const hasDiscount = product.sale_price && product.sale_price < product.price;
  const isLowStock = product.inventory_quantity > 0 && product.inventory_quantity <= 5;
  
  // Calculate sale percentage
  const salePercent = hasDiscount 
    ? Math.round((1 - product.sale_price / product.price) * 100) 
    : 0;
  const savings = hasDiscount ? product.price - product.sale_price : 0;

  return (
    <Link to={`/product/${product.id}`}>
      <Card className="group relative bg-card rounded-2xl border border-border/50 overflow-hidden hover:shadow-xl hover:shadow-green-900/5 transition-all duration-500" data-testid={`product-card-${product.id}`}>
        <div className="aspect-square relative overflow-hidden bg-secondary/30">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          
          {/* Sale Badge - Eye-catching! */}
          {hasDiscount && (
            <div className="absolute top-3 left-3 flex flex-col gap-1" data-testid="sale-badge">
              <div className="bg-red-500 text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg flex items-center gap-1 animate-pulse">
                <Tag className="w-3.5 h-3.5" />
                {salePercent}% OFF
              </div>
            </div>
          )}
          
          {/* Low Stock Badge */}
          {isLowStock && (
            <div className="absolute top-3 right-3" data-testid="low-stock-badge">
              <div className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Only {product.inventory_quantity} left!
              </div>
            </div>
          )}
          
          {/* Out of Stock Overlay */}
          {product.inventory_quantity === 0 && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center" data-testid="out-of-stock">
              <span className="text-white text-lg font-semibold">Out of Stock</span>
            </div>
          )}
        </div>
        
        <CardContent className="p-4 md:p-6">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{product.category}</p>
          <h3 className="heading-font text-lg md:text-xl font-semibold mb-2 line-clamp-2" data-testid="product-name">{product.name}</h3>
          
          <div className="flex items-end justify-between mt-3 gap-2">
            <div className="min-w-0 flex-1">
              {hasDiscount ? (
                <>
                  {/* Sale Price - Bright Red */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-2xl md:text-3xl font-bold text-red-500" data-testid="product-price">
                      ${displayPrice.toFixed(2)}
                    </span>
                    <span className="text-sm text-muted-foreground line-through decoration-2">
                      ${product.price.toFixed(2)}
                    </span>
                  </div>
                  {/* Save Amount Badge */}
                  <span className="inline-block mt-1 text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded">
                    SAVE ${savings.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-2xl md:text-3xl font-bold text-primary" data-testid="product-price">
                  ${displayPrice.toFixed(2)}
                </span>
              )}
            </div>
            
            <Button
              onClick={handleAddToCart}
              disabled={product.inventory_quantity === 0}
              className="rounded-full transition-all hover:scale-105 bg-primary text-primary-foreground hover:bg-primary/90 h-10 w-10 flex-shrink-0"
              size="icon"
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ProductCard;
